import type { SpotView } from "@auto-pools/core";
import { formatMoney } from "../../lib/format";

/**
 * "Which coins, without a click" (design 50): a ticker plus its result for every held coin,
 * grouped calm/season, free USDT at the end. A coin a slice still holds past its own review day
 * (dropped from the ranking, still owned) is drawn faded — the server marks it by listing it in
 * `standingExits`/leaving via the slice holdings it is no longer newly picked for; since the view
 * does not carry a per-coin "result" percent, the chip shows the coin and its value share only
 * (no invented percent change) when a per-coin result is not provided.
 */
export function SpotCoinChipsRow({ spot }: { spot: SpotView }) {
  if (spot.coins.length === 0 && spot.foreign.length === 0) return null;
  const leaving = new Set(spot.standingExits.map((exit) => exit.coin));
  const calm = spot.slices.filter((slice) => slice.half === "calm");
  const season = spot.slices.filter((slice) => slice.half === "season");
  const inHalf = (coin: string, half: typeof calm) =>
    half.some(
      (slice) =>
        slice.holdings.some((h) => h.coin === coin) || slice.dust.some((h) => h.coin === coin),
    );
  const calmCoins = spot.coins.filter((coin) => inHalf(coin.coin, calm));
  const seasonCoins = spot.coins.filter(
    (coin) => inHalf(coin.coin, season) && !calmCoins.includes(coin),
  );
  const other = spot.coins.filter(
    (coin) => !calmCoins.includes(coin) && !seasonCoins.includes(coin),
  );

  function Chip({ coin }: { coin: SpotView["coins"][number] }) {
    const fading = leaving.has(coin.coin);
    return (
      <span className={`spot-coin-chip${fading ? " fading" : ""}`} key={coin.coin}>
        <span className="spot-coin-chip-name">{coin.coin}</span>
        <span className={`num spot-coin-chip-value ${coin.value === null ? "unknown" : ""}`}>
          {coin.value === null ? "—" : formatMoney(coin.value)}
        </span>
      </span>
    );
  }

  return (
    <div className="spot-coin-row">
      {calmCoins.length > 0 && (
        <>
          <span className="spot-coin-group-dot calm" aria-hidden="true" />
          <span className="spot-coin-group-label">calm</span>
          {calmCoins.map((coin) => (
            <Chip coin={coin} key={coin.coin} />
          ))}
        </>
      )}
      {seasonCoins.length > 0 && (
        <>
          {calmCoins.length > 0 && <span className="spot-coin-sep" aria-hidden="true" />}
          <span className="spot-coin-group-dot season" aria-hidden="true" />
          <span className="spot-coin-group-label">season</span>
          {seasonCoins.map((coin) => (
            <Chip coin={coin} key={coin.coin} />
          ))}
        </>
      )}
      {other.map((coin) => (
        <Chip coin={coin} key={coin.coin} />
      ))}
      <div className="spot-coin-row-fill" />
      {leaving.size > 0 && <span className="spot-coin-row-legend">faded = leaving</span>}
      <span className="num spot-coin-row-usdt">
        {spot.card.freeUsdt === null
          ? "Free USDT —"
          : `Free USDT ${formatMoney(spot.card.freeUsdt)}`}
      </span>
    </div>
  );
}
